import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Skeleton,
  Checkbox,
  LinearProgress,
  Link,
  alpha,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import EditIcon from '@mui/icons-material/Edit';
import AddCircle from '@mui/icons-material/AddCircle';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import RoomIcon from '@mui/icons-material/Room';
import AllChannelsSelector, {
  DEFAULT_RATE_CHANNEL_SELECTION,
} from '../common/AllChannelsSelector';
import { getConfigurationRooms } from '../../services/api';
import { fetchBlockRoomAvailability } from '../../services/inventoryBlockService';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import { formatHotelDate, getHotelDateFormat } from '../../utils/dateUtils';
import {
  getDrawerDatePickerSlotProps,
  getDrawerSelectMenuProps,
} from '../../utils/drawerOverlayProps';


const THEME_COLOR = '#4ce1b6';

export const EVENT_BLOCK_TYPES = [
  { value: 'EVENT', label: 'Event' },
  { value: 'OVERBOOKING', label: 'Overbooking Protection' },
  { value: 'MANUAL', label: 'Manual Block' },
  { value: 'ENQUIRY', label: 'Enquiry' },
  { value: 'OTHER', label: 'Other' },
];

export function normalizeRoomTypeId(value) {
  if (value === '' || value == null) return '';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? String(value) : numeric;
}

function flattenRoomsByFloor(floors) {
  if (!Array.isArray(floors)) return [];
  const rooms = [];
  floors.forEach((floor) => {
    (floor.rooms || []).forEach((room) => {
      rooms.push({
        id: Number(room.id),
        room_number: room.room_number,
        room_type_id: Number(room.room_type_id),
        room_type_name: room.room_type_name || '',
        room_type_image_url: room.room_type_image_url || null,
        status: room.status,
      });
    });
  });
  return rooms.sort((a, b) => {
    const numA = parseInt(String(a.room_number).replace(/\D/g, ''), 10) || a.id;
    const numB = parseInt(String(b.room_number).replace(/\D/g, ''), 10) || b.id;
    return numA - numB;
  });
}

/** Card-shaped skeleton matching Select Room tiles */
function RoomSelectSkeleton({ isDark, count = 4 }) {
  return (
    <Grid container spacing={1.5} aria-busy="true" aria-label="Loading rooms">
      {Array.from({ length: count }, (_, i) => (
        <Grid item xs={6} sm={3} key={`room-skel-${i}`}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              opacity: 0,
              animation: 'roomSkelIn 0.35s ease forwards',
              animationDelay: `${Math.min(i, 6) * 55}ms`,
              '@keyframes roomSkelIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={120}
              animation="wave"
              sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
            />
            <Box sx={{ p: 1.25, display: 'flex', justifyContent: 'center' }}>
              <Skeleton variant="rounded" width="70%" height={14} animation="wave" />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

const RoomCardImage = memo(function RoomCardImage({ src, isDark, isDisabled }) {
  const [loaded, setLoaded] = useState(!src);
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 120,
        position: 'relative',
        backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
        backgroundImage:
          !showImage
            ? `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`
            : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: isDisabled ? 'grayscale(0.7)' : 'none',
        overflow: 'hidden',
      }}
    >
      {showImage && (
        <Box
          component="img"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setFailed(true);
            setLoaded(true);
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.28s ease',
          }}
        />
      )}
      {showImage && !loaded && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: 'absolute', inset: 0, height: '100%' }}
        />
      )}
      {!showImage && (
        <RoomIcon
          sx={{
            fontSize: 32,
            color: isDark ? 'rgba(255,255,255,0.35)' : '#999',
            opacity: 0.5,
          }}
        />
      )}
    </Box>
  );
});

const EventBlockAdjustView = ({
  mode,
  onModeChange,
  inventoryBlocks,
  loading,
  isSaving = false,
  onRefresh,
  onEdit,
  onToggleActive,
  formData,
  onFormDataChange,
  errors,
  onErrorsChange,
  allRoomTypes,
  isDark,
  
  channels,
  getInventoryData,
  editingBlock = null,
}) => {
  const themeColors = getDashboardDarkSurfaceTokens(isDark);
  const fieldSurface = isDark ? themeColors.surface : '#ffffff';
  const selectMenuProps = useMemo(
    () => getDrawerSelectMenuProps(isDark, fieldSurface),
    [isDark, fieldSurface]
  );
  const datePickerSlotProps = useMemo(
    () => getDrawerDatePickerSlotProps(isDark, fieldSurface),
    [isDark, fieldSurface]
  );
  const [activeTab, setActiveTab] = useState(mode === 'add' ? 0 : 1);
  // Track if we've already loaded delete data to prevent re-fetching
  const hasLoadedDeleteData = useRef(false);
  const [allRooms, setAllRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [bookedRoomIds, setBookedRoomIds] = useState(() => new Set());
  const [capacityByRoomType, setCapacityByRoomType] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectionHint, setSelectionHint] = useState(null);
  const availabilityCacheRef = useRef({});
  const roomsCacheRef = useRef(null);
  const hasChannels = Array.isArray(channels) && channels.length > 0;

  useEffect(() => {
    if (editingBlock || mode !== 'add') return;
    const startDate = formData.start_date && moment.isMoment(formData.start_date)
      ? formData.start_date
      : (formData.start_date ? moment(formData.start_date) : moment());
    const targetEndDate = startDate.clone().add(1, 'day');
    const currentEndDate = formData.end_date && moment.isMoment(formData.end_date)
      ? formData.end_date
      : (formData.end_date ? moment(formData.end_date) : null);
    const startTime = formData.start_time && moment.isMoment(formData.start_time)
      ? formData.start_time
      : (formData.start_time ? moment(formData.start_time, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true) : moment().seconds(0).milliseconds(0));
    const validStartTime = startTime?.isValid?.() ? startTime : moment().seconds(0).milliseconds(0);
    const endTime = formData.end_time && moment.isMoment(formData.end_time)
      ? formData.end_time
      : (formData.end_time ? moment(formData.end_time, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true) : null);

    // Default unblock to next day only when missing/invalid — never clamp a later user pick.
    const needsEndDate =
      !currentEndDate ||
      !currentEndDate.isValid() ||
      !currentEndDate.isAfter(startDate, 'day');
    const needsStartTime = !formData.start_time || !validStartTime.isValid();
    const needsEndTime = !endTime || !endTime.isValid() || !endTime.isSame(validStartTime, 'minute');

    if (!needsEndDate && !needsStartTime && !needsEndTime) return;
    onFormDataChange((prev) => ({
      ...prev,
      start_date: startDate,
      ...(needsEndDate ? { end_date: targetEndDate } : {}),
      ...(needsStartTime ? { start_time: validStartTime } : {}),
      ...(needsEndTime ? { end_time: validStartTime.clone() } : {}),
    }));
  }, [
    editingBlock,
    formData.end_date,
    formData.end_time,
    formData.start_date,
    formData.start_time,
    mode,
    onFormDataChange,
  ]);
  
  useEffect(() => {
    let cancelled = false;
    const loadRooms = async () => {
      if (roomsCacheRef.current) {
        setAllRooms(roomsCacheRef.current);
        setRoomsLoading(false);
        return;
      }
      setRoomsLoading(true);
      try {
        const floors = await getConfigurationRooms();
        if (!cancelled) {
          const flat = flattenRoomsByFloor(floors);
          roomsCacheRef.current = flat;
          setAllRooms(flat);
        }
      } catch {
        if (!cancelled) setAllRooms([]);
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    };
    loadRooms();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load allocated rooms + capacity for date range (debounced; keep cards visible while refreshing)
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const applyAvailability = (data) => {
      const bookedSet = new Set((data.room_ids || []).map((id) => Number(id)));
      const capacity = data.capacity_by_room_type || {};
      setBookedRoomIds(bookedSet);
      setCapacityByRoomType(capacity);
      setSelectionHint(null);

      onFormDataChange((prev) => {
        const currentSelected = Array.isArray(prev.selected_room_ids)
          ? prev.selected_room_ids.map((id) => Number(id)).filter(Boolean)
          : [];
        let nextSelected = currentSelected.filter((id) => !bookedSet.has(id));
        const byType = {};
        nextSelected.forEach((id) => {
          const room = allRooms.find((r) => Number(r.id) === Number(id));
          if (!room) return;
          const typeKey = String(room.room_type_id);
          if (!byType[typeKey]) byType[typeKey] = [];
          byType[typeKey].push(id);
        });
        const trimmed = [];
        Object.entries(byType).forEach(([typeKey, ids]) => {
          const maxBlockable = Number(capacity[typeKey]?.max_blockable);
          const limit = Number.isFinite(maxBlockable) ? maxBlockable : ids.length;
          trimmed.push(...ids.slice(0, Math.max(0, limit)));
        });
        nextSelected.forEach((id) => {
          if (!allRooms.some((r) => Number(r.id) === Number(id)) && !trimmed.includes(id)) {
            trimmed.push(id);
          }
        });
        const same =
          trimmed.length === currentSelected.length &&
          trimmed.every((id, idx) => id === currentSelected[idx]);
        if (same) return prev;
        return {
          ...prev,
          selected_room_ids: trimmed,
          num_rooms: Math.max(1, trimmed.length),
        };
      });
    };

    const loadAvailability = async () => {
      if (!formData.start_date || !formData.end_date) {
        setBookedRoomIds(new Set());
        setCapacityByRoomType({});
        setSelectionHint(null);
        setAvailabilityLoading(false);
        return;
      }

      const startDateStr = moment(formData.start_date).format('YYYY-MM-DD');
      const endDateStr = moment(formData.end_date).format('YYYY-MM-DD');
      const cacheKey = `${startDateStr}|${endDateStr}|${editingBlock?.id || 'new'}`;

      if (availabilityCacheRef.current[cacheKey]) {
        applyAvailability(availabilityCacheRef.current[cacheKey]);
        setAvailabilityLoading(false);
        return;
      }

      setAvailabilityLoading(true);
      try {
        const data = await fetchBlockRoomAvailability(startDateStr, endDateStr, {
          excludeBlockId: editingBlock?.id,
        });
        if (cancelled) return;
        availabilityCacheRef.current[cacheKey] = data;
        applyAvailability(data);
      } catch {
        if (!cancelled) {
          setBookedRoomIds(new Set());
          setCapacityByRoomType({});
        }
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    };

    // Debounce date-range changes so rapid picker updates don't spam the API
    timer = setTimeout(loadAvailability, 280);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.start_date, formData.end_date, editingBlock?.id, allRooms.length]);

  useEffect(() => {
    if (mode === 'delete') {
      setActiveTab(1);
      // Only refresh if we haven't loaded delete data yet
      if (!hasLoadedDeleteData.current) {
        onRefresh();
        hasLoadedDeleteData.current = true;
      }
    } else {
      setActiveTab(0);
      hasLoadedDeleteData.current = false; // Reset when switching away from delete
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    onModeChange(newValue === 0 ? 'add' : 'delete');
    if (newValue === 1) {
      // Only refresh when switching to delete tab if we haven't loaded yet
      if (!hasLoadedDeleteData.current) {
        onRefresh();
        hasLoadedDeleteData.current = true;
      }
    } else {
      hasLoadedDeleteData.current = false; // Reset when switching away
    }
  };

  const selectedRoomIds = Array.isArray(formData.selected_room_ids)
    ? formData.selected_room_ids.map((id) => Number(id)).filter(Boolean)
    : [];
  const isEditing = Boolean(editingBlock);

  const handleInputChange = (field, value) => {
    onFormDataChange({ ...formData, [field]: value });
    if (errors[field]) {
      onErrorsChange({ ...errors, [field]: null });
    }
  };

  const handleRoomTypeChange = (value) => {
    const nextTypeId = normalizeRoomTypeId(value);
    // Editing a single block: clear rooms that don't match the new type
    let nextSelected = selectedRoomIds;
    if (isEditing) {
      nextSelected = selectedRoomIds.filter((id) => {
        const room = allRooms.find((r) => r.id === id);
        return room && Number(room.room_type_id) === Number(nextTypeId);
      });
    }
    onFormDataChange({
      ...formData,
      room_type_id: nextTypeId,
      selected_room_ids: nextSelected,
      num_rooms: Math.max(1, nextSelected.length),
    });
    if (errors.room_type_id || errors.selected_room_ids) {
      onErrorsChange({ ...errors, room_type_id: null, selected_room_ids: null });
    }
  };

  const toggleRoomSelection = useCallback((roomId) => {
    const id = Number(roomId);
    if (bookedRoomIds.has(id)) {
      return;
    }
    const room = allRooms.find((r) => Number(r.id) === id);
    const isSelected = selectedRoomIds.includes(id);
    if (!isSelected && room) {
      const typeKey = String(room.room_type_id);
      const capacity = capacityByRoomType[typeKey] || {};
      const maxBlockable = Number(capacity.max_blockable);
      const selectedOfType = selectedRoomIds.filter((sid) => {
        const match = allRooms.find((r) => Number(r.id) === Number(sid));
        return match && Number(match.room_type_id) === Number(room.room_type_id);
      });
      if (Number.isFinite(maxBlockable) && selectedOfType.length >= maxBlockable) {
        const booked = Number(capacity.booked) || 0;
        setSelectionHint(
          booked > 0
            ? `Only ${maxBlockable} of ${capacity.total || selectedOfType.length + 1} rooms can be blocked — ${booked} already booked for these dates.`
            : `Only ${maxBlockable} room(s) can be blocked for this type.`
        );
        return;
      }
    }
    setSelectionHint(null);
    let nextIds;
    if (isSelected) {
      nextIds = selectedRoomIds.filter((rid) => rid !== id);
    } else {
      nextIds = [...selectedRoomIds, id];
    }
    onFormDataChange({
      ...formData,
      selected_room_ids: nextIds,
      num_rooms: Math.max(1, nextIds.length),
    });
    if (errors.selected_room_ids || errors.num_rooms || errors.room_ids) {
      onErrorsChange({ ...errors, selected_room_ids: null, num_rooms: null, room_ids: null });
    }
  }, [
    bookedRoomIds,
    allRooms,
    selectedRoomIds,
    formData,
    onFormDataChange,
    errors,
    onErrorsChange,
    capacityByRoomType,
    selectionHint,
  ]);

  const handleDateChange = (field) => (date) => {
    let next = { ...formData, [field]: date };
    if (field === 'start_date' && date) {
      next.end_date = date.clone().add(1, 'day');
    }
    onFormDataChange(next);
    if (errors[field]) {
      onErrorsChange({ ...errors, [field]: null });
    }
  };

  const handleTimeChange = (field) => (time) => {
    onFormDataChange({
      ...formData,
      [field]: time,
      ...(field === 'start_time' ? { end_time: time } : {}),
    });
    if (errors[field]) {
      onErrorsChange({ ...errors, [field]: null });
    }
  };

  const startTimeValue = useMemo(() => {
    const t = formData.start_time;
    if (!t) return null;
    if (moment.isMoment(t)) return t;
    const parsed = moment(t, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true);
    return parsed.isValid() ? parsed : null;
  }, [formData.start_time]);

  const endTimeValue = useMemo(() => {
    const t = formData.end_time;
    if (!t) return null;
    if (moment.isMoment(t)) return t;
    const parsed = moment(t, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true);
    return parsed.isValid() ? parsed : null;
  }, [formData.end_time]);

  const roomsForSelectedType = useMemo(
    () =>
      allRooms.filter(
        (room) =>
          formData.room_type_id !== '' &&
          formData.room_type_id != null &&
          Number(room.room_type_id) === Number(formData.room_type_id)
      ),
    [allRooms, formData.room_type_id]
  );

  const selectedRoomsMeta = useMemo(
    () =>
      selectedRoomIds
        .map((id) => allRooms.find((r) => r.id === id))
        .filter(Boolean),
    [selectedRoomIds, allRooms]
  );

  const currentTypeCapacity =
    formData.room_type_id != null && formData.room_type_id !== ''
      ? capacityByRoomType[String(formData.room_type_id)]
      : null;
  const selectedForCurrentType = selectedRoomIds.filter((id) => {
    const room = allRooms.find((r) => Number(r.id) === Number(id));
    return room && Number(room.room_type_id) === Number(formData.room_type_id);
  }).length;

  // Cold load only — keep cards visible while availability refreshes
  const showRoomSkeleton = roomsLoading && allRooms.length === 0;
  const showAvailabilityOverlay =
    availabilityLoading && allRooms.length > 0 && Boolean(formData.start_date && formData.end_date);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatHotelDate(dateString);
    } catch {
      return dateString;
    }
  };

  const getBlockTypeColor = (blockType) => {
    const colors = {
      MAINTENANCE: 'warning',
      EVENT: 'info',
      OVERBOOKING: 'error',
      MANUAL: 'default',
      ENQUIRY: 'primary',
      OTHER: 'secondary',
    };
    return colors[blockType] || 'default';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: `1px solid ${themeColors.surfaceBorder}`,
          mb: 2,
          '& .MuiTab-root': {
            color: isDark ? '#999' : '#666',
            '&.Mui-selected': {
              color: THEME_COLOR,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: THEME_COLOR,
          },
        }}
      >
        <Tab label="Add Event" icon={<AddCircle />} iconPosition="start" />
        <Tab label="Manage Event" icon={<RemoveCircle />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 ? (
        // Add Inventory Form
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1, pt: 1.25, '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: isDark ? '#555' : '#ccc', borderRadius: '4px' } }}>
          <Grid container spacing={2} sx={{ width: '100%', m: 0, overflow: 'visible', pr: 0.5 }}>
            <Grid item xs={12} sx={{ pt: '4px !important' }}>
              <FormControl fullWidth size="small" error={!!errors.room_type_id} required sx={{ overflow: 'visible' }}>
                <InputLabel id="event-block-room-type-label" shrink={Boolean(normalizeRoomTypeId(formData.room_type_id))}>
                  Room Type
                </InputLabel>
                <Select
                  labelId="event-block-room-type-label"
                  value={normalizeRoomTypeId(formData.room_type_id)}
                  onChange={(e) => handleRoomTypeChange(e.target.value)}
                  label="Room Type"
                  MenuProps={selectMenuProps}
                >
                  {allRoomTypes.map((roomType) => (
                    <MenuItem key={roomType.id} value={normalizeRoomTypeId(roomType.id)}>
                      {roomType.name || `Room Type ${roomType.id}`}
                    </MenuItem>
                  ))}
                </Select>
                {errors.room_type_id && <FormHelperText>{errors.room_type_id}</FormHelperText>}
                <FormHelperText sx={{ mt: 0.5 }}>
                  {isEditing
                    ? 'Change room type to pick rooms of that type for this block'
                    : 'Switch room type to select rooms from other types; selections are kept'}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Block Type{' '}
                <Typography component="span" sx={{ color: 'error.main' }}>
                  *
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {EVENT_BLOCK_TYPES.map((type) => {
                  const isSelected = formData.block_type === type.value;
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      onClick={() => handleInputChange('block_type', type.value)}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: isSelected ? 600 : 400,
                        backgroundColor: isSelected ? THEME_COLOR : 'transparent',
                        color: isSelected ? '#000' : 'inherit',
                        borderColor: isSelected ? THEME_COLOR : 'inherit',
                        '&:hover': {
                          backgroundColor: isSelected ? THEME_COLOR : alpha(THEME_COLOR, 0.1),
                          borderColor: THEME_COLOR,
                        },
                      }}
                    />
                  );
                })}
              </Box>
              {errors.block_type && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {errors.block_type}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <DatePicker
                        label="Block date"
                        format={getHotelDateFormat()}
                        value={formData.start_date}
                        onChange={handleDateChange('start_date')}
                        minDate={moment().startOf('day')}
                        slotProps={{
                          ...datePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.start_date,
                            helperText: errors.start_date,
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <TimePicker
                        label="Block time"
                        value={startTimeValue}
                        onChange={handleTimeChange('start_time')}
                        ampm
                        timeSteps={{ minutes: 15 }}
                        views={['hours', 'minutes']}
                        slotProps={{
                          ...datePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.start_time,
                            helperText: errors.start_time || 'When the block starts',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <DatePicker
                        label="Unblock date"
                        format={getHotelDateFormat()}
                        value={formData.end_date}
                        onChange={handleDateChange('end_date')}
                        minDate={formData.start_date ? formData.start_date.clone().add(1, 'day') : undefined}
                        slotProps={{
                          ...datePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.end_date,
                            helperText: errors.end_date || 'Room becomes available again on this date',
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <TimePicker
                        label="Unblock time"
                        value={endTimeValue}
                        onChange={handleTimeChange('end_time')}
                        ampm
                        timeSteps={{ minutes: 15 }}
                        views={['hours', 'minutes']}
                        slotProps={{
                          ...datePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.end_time,
                            helperText: errors.end_time || 'When the block ends',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: themeColors.text, fontWeight: 600, mb: 1 }}>
                Select Room
              </Typography>
              {currentTypeCapacity && Number(currentTypeCapacity.booked) > 0 && (
                <Typography variant="caption" sx={{ color: themeColors.subtleText || 'text.secondary', display: 'block', mb: 1 }}>
                  {currentTypeCapacity.booked} booking(s) hold inventory — you can block up to {currentTypeCapacity.max_blockable} of {currentTypeCapacity.total} rooms
                  {selectedForCurrentType > 0 ? ` (${selectedForCurrentType} selected)` : ''}.
                </Typography>
              )}
              {selectionHint && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mb: 1 }}>
                  {selectionHint}
                </Typography>
              )}
              {!formData.room_type_id ? (
                <Typography variant="caption" color="text.secondary">
                  Select a room type to see rooms
                </Typography>
              ) : !formData.start_date || !formData.end_date ? (
                <Typography variant="caption" color="text.secondary">
                  Select dates to see which rooms are available to block
                </Typography>
              ) : showRoomSkeleton ? (
                <RoomSelectSkeleton
                  isDark={isDark}
                  count={Math.max(4, Math.min(8, roomsForSelectedType.length || 4))}
                />
              ) : roomsForSelectedType.length === 0 && !roomsLoading ? (
                <Typography variant="caption" color="text.secondary">
                  No rooms found for this room type
                </Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  {showAvailabilityOverlay && (
                    <LinearProgress
                      sx={{
                        position: 'absolute',
                        top: -6,
                        left: 0,
                        right: 0,
                        height: 3,
                        borderRadius: 2,
                        zIndex: 2,
                        bgcolor: alpha(THEME_COLOR, 0.15),
                        '& .MuiLinearProgress-bar': { bgcolor: THEME_COLOR },
                      }}
                    />
                  )}
                <Grid
                  container
                  spacing={1.5}
                  sx={{
                    opacity: showAvailabilityOverlay ? 0.72 : 1,
                    transition: 'opacity 0.22s ease',
                    pointerEvents: showAvailabilityOverlay ? 'none' : 'auto',
                  }}
                >
                  {roomsForSelectedType.map((room, roomIndex) => {
                    const isBooked = bookedRoomIds.has(Number(room.id));
                    const typeKey = String(room.room_type_id);
                    const maxBlockable = Number(capacityByRoomType[typeKey]?.max_blockable);
                    const atCapacity =
                      Number.isFinite(maxBlockable) && selectedForCurrentType >= maxBlockable;
                    const isSelected = !isBooked && selectedRoomIds.includes(room.id);
                    const isDisabled = isBooked || (atCapacity && !isSelected);
                    const roomTypeMeta = allRoomTypes.find(
                      (rt) => Number(rt.id) === Number(room.room_type_id)
                    );
                    const roomImage =
                      room.room_type_image_url ||
                      roomTypeMeta?.image ||
                      roomTypeMeta?.image_url ||
                      null;
                    const roomTypeLabel =
                      room.room_type_name ||
                      roomTypeMeta?.name ||
                      `Room ${room.room_number ?? room.id}`;
                    return (
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        key={room.id}
                        sx={{
                          opacity: 0,
                          animation: 'roomCardIn 0.3s ease forwards',
                          animationDelay: `${Math.min(roomIndex, 8) * 40}ms`,
                          '@keyframes roomCardIn': {
                            from: { opacity: 0, transform: 'translateY(4px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                          },
                        }}
                      >
                        <Card
                          elevation={1}
                          onClick={() => {
                            if (!isDisabled || isSelected) toggleRoomSelection(room.id);
                          }}
                          sx={{
                            backgroundColor: isDark ? themeColors.surface : '#ffffff',
                            borderRadius: 3,
                            transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.2s ease, opacity 0.2s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: isDisabled && !isSelected ? 'not-allowed' : 'pointer',
                            opacity: isDisabled && !isSelected ? 0.55 : 1,
                            border: isSelected
                              ? `2px solid ${THEME_COLOR}`
                              : `1px solid ${themeColors.surfaceBorder}`,
                            '&:hover': isDisabled && !isSelected
                              ? {}
                              : {
                                  transform: 'translateY(-2px)',
                                  boxShadow: isDark
                                    ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                                    : '0 8px 16px rgba(76, 225, 182, 0.2)',
                                },
                          }}
                        >
                          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            <Box
                              sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Box sx={{ position: 'relative' }}>
                                <RoomCardImage
                                  src={roomImage}
                                  isDark={isDark}
                                  isDisabled={isDisabled && !isSelected}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '50%',
                                    background: `linear-gradient(180deg, 
                                      ${isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)'} 0%, 
                                      ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.4)'} 50%, 
                                      transparent 100%)`,
                                    pointerEvents: 'none',
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    right: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    zIndex: 2,
                                  }}
                                >
                                  {!isBooked && (
                                    <Checkbox
                                      checked={isSelected}
                                      size="small"
                                      disabled={isDisabled && !isSelected}
                                      sx={{
                                        color: '#fff',
                                        backgroundColor: isSelected
                                          ? 'rgba(76, 225, 182, 0.9)'
                                          : 'rgba(0, 0, 0, 0.4)',
                                        borderRadius: '4px',
                                        padding: '2px',
                                        backdropFilter: 'blur(8px)',
                                        '&.Mui-checked': {
                                          color: '#000',
                                          backgroundColor: 'rgba(76, 225, 182, 0.9)',
                                        },
                                        '&.Mui-disabled': {
                                          color: 'rgba(255,255,255,0.4)',
                                          backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                        },
                                        '&:hover': {
                                          backgroundColor: isSelected
                                            ? 'rgba(76, 225, 182, 1)'
                                            : 'rgba(0, 0, 0, 0.6)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 20,
                                          filter: isSelected
                                            ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                            : 'none',
                                        },
                                        transition: 'all 0.3s ease',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isDisabled || isSelected) toggleRoomSelection(room.id);
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                    label={room.room_number ?? room.id}
                                    size="small"
                                    sx={{
                                      backgroundColor: isBooked
                                        ? 'rgba(120, 120, 120, 0.85)'
                                        : isSelected
                                          ? 'rgba(76, 225, 182, 0.9)'
                                          : 'rgba(0, 0, 0, 0.5)',
                                      color: '#fff',
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                      height: 28,
                                      backdropFilter: 'blur(10px)',
                                      border: `1.5px solid ${
                                        isBooked
                                          ? 'rgba(255, 255, 255, 0.2)'
                                          : isSelected
                                            ? 'rgba(76, 225, 182, 1)'
                                            : 'rgba(255, 255, 255, 0.3)'
                                      }`,
                                      letterSpacing: '0.5px',
                                      '& .MuiChip-icon': {
                                        color: '#fff !important',
                                        marginLeft: '8px',
                                      },
                                    }}
                                  />
                                  {isBooked && (
                                    <Chip
                                      label="Booked"
                                      size="small"
                                      sx={{
                                        ml: 'auto',
                                        height: 24,
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'rgba(211, 47, 47, 0.92)',
                                        color: '#fff',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.25,
                                  backgroundColor: isDark
                                    ? 'rgba(42, 42, 49, 0.95)'
                                    : 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(10px)',
                                  borderTop: `1px solid ${
                                    isDark
                                      ? 'rgba(76, 225, 182, 0.2)'
                                      : 'rgba(76, 225, 182, 0.15)'
                                  }`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? themeColors.text : '#333',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    display: 'block',
                                    textAlign: 'center',
                                  }}
                                >
                                  {roomTypeLabel}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                </Box>
              )}
              {selectedRoomsMeta.filter(
                (room) => Number(room.room_type_id) !== Number(formData.room_type_id)
              ).length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Also selected from other types:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedRoomsMeta
                      .filter((room) => Number(room.room_type_id) !== Number(formData.room_type_id))
                      .map((room) => (
                        <Chip
                          key={`selected-other-${room.id}`}
                          label={`${room.room_number}${room.room_type_name ? ` · ${room.room_type_name}` : ''}`}
                          size="small"
                          onDelete={() => toggleRoomSelection(room.id)}
                          sx={{
                            backgroundColor: alpha(THEME_COLOR, 0.2),
                            borderColor: THEME_COLOR,
                          }}
                          variant="outlined"
                        />
                      ))}
                  </Box>
                </Box>
              )}
              {(errors.selected_room_ids || errors.room_ids) && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                  {errors.selected_room_ids || errors.room_ids}
                </Typography>
              )}
              {selectedRoomIds.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedRoomIds.length} {selectedRoomIds.length === 1 ? 'room' : 'rooms'} selected
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reason (Optional)"
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                error={!!errors.reason}
                helperText={errors.reason}
                fullWidth
                size="small"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <AllChannelsSelector
                channels={channels}
                value={(() => {
                  const sel = formData.selectedChannels || [];
                  if (sel.includes('all')) {
                    return sel.includes('pms') ? ['pms', 'all'] : ['all'];
                  }
                  if (sel.length === 0) return [];
                  // Legacy OTA id lists promote to All Channels; PMS only if explicitly present
                  const hasOtaIds = sel.some((id) => id !== 'pms');
                  const pmsPart = sel.includes('pms') ? ['pms'] : [];
                  return hasOtaIds ? [...pmsPart, 'all'] : pmsPart;
                })()}
                onChange={(next) => handleInputChange('selectedChannels', next)}
                allowPms
                pmsMode="toggle"
                otaMode="display"
                themeColor={THEME_COLOR}
                helperText={
                  hasChannels
                    ? 'All Channels selects every OTA. PMS is optional. Individual OTAs cannot be toggled.'
                    : 'No OTA channels configured. PMS is the only available target.'
                }
              />
              {errors.selectedChannels && (
                <FormHelperText error sx={{ mt: 0.5 }}>{errors.selectedChannels}</FormHelperText>
              )}
              <FormHelperText sx={{ mt: 0.5 }}>
                {hasChannels
                  ? 'All Channels applies this block to every OTA; include PMS only if selected'
                  : 'This block can only target PMS until an OTA channel is configured.'}
              </FormHelperText>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // Manage Event - Show active-today blocks in cards
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1, '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: isDark ? '#555' : '#ccc', borderRadius: '4px' } }}>
          {(loading || inventoryBlocks.length > 0) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Link
                component={RouterLink}
                to="/distribution/inventory-block/"
                underline="hover"
                sx={{
                  color: THEME_COLOR,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                }}
              >
                View all inventory blocks
              </Link>
            </Box>
          )}
          {loading && inventoryBlocks.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={56} animation="wave" sx={{ borderRadius: 1.5 }} />
              ))}
            </Box>
          ) : inventoryBlocks.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                No inventory blocks covering today.
              </Typography>
              <Link
                component={RouterLink}
                to="/distribution/inventory-block/"
                underline="hover"
                sx={{
                  color: THEME_COLOR,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                View all inventory blocks
              </Link>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }}>
              {loading && (
                <LinearProgress
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                    mb: 0.25,
                  }}
                />
              )}
              {inventoryBlocks.map((block) => {
                const channelIds = Array.isArray(block.channel_ids) ? block.channel_ids : [];
                const otaNames = channelIds
                  .map((id) => channels?.find((ch) => Number(ch.id) === Number(id))?.name || `Channel ${id}`)
                  .filter(Boolean);
                const scopeParts = [];
                if (block.include_pms) scopeParts.push('PMS');
                if (otaNames.length > 0) scopeParts.push(...otaNames);
                const scopeLabel = scopeParts.length > 0 ? scopeParts.join(' · ') : 'No channels';
                const roomNumbers = Array.isArray(block.rooms) && block.rooms.length > 0
                  ? block.rooms.map((r) => r.room_number).join(', ')
                  : null;
                const metaLine = [
                  `${formatDate(block.start_date)} – ${formatDate(block.end_date)}`,
                  `${block.num_rooms} rm${roomNumbers ? ` (${roomNumbers})` : ''}`,
                ].join(' · ');

                return (
                  <Card
                    key={block.id}
                    elevation={0}
                    sx={{
                      width: '100%',
                      backgroundColor: isDark ? themeColors.surface : '#ffffff',
                      border: `1px solid ${themeColors.surfaceBorder}`,
                      opacity: block.is_active ? 1 : 0.72,
                      '&:hover': { boxShadow: 1 },
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      {/* Row 1: title + chips + actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.2 }}
                            noWrap
                          >
                            {block.room_type_name || `Room Type ${block.room_type_id}`}
                          </Typography>
                          <Chip
                            label={block.block_type_display || block.block_type}
                            size="small"
                            color={getBlockTypeColor(block.block_type)}
                            sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
                          />
                          <Chip
                            label={block.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={block.is_active ? 'success' : 'default'}
                            sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(block)}
                              sx={{
                                color: THEME_COLOR,
                                p: 0.35,
                                '&:hover': { bgcolor: alpha(THEME_COLOR, 0.15) },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={block.is_active ? 'Disable block' : 'Enable block'}>
                            <Switch
                              size="small"
                              checked={Boolean(block.is_active)}
                              onChange={() => onToggleActive?.(block)}
                              disabled={loading || isSaving || !onToggleActive}
                              color="success"
                              sx={{ ml: 0.25 }}
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Row 2: dates + rooms */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.35,
                          fontSize: '0.7rem',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={metaLine}
                      >
                        {metaLine}
                      </Typography>

                      {/* Row 3: channels / OTAs — full names */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.15,
                          fontSize: '0.7rem',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                        title={scopeLabel}
                      >
                        {scopeLabel}
                      </Typography>

                      {/* Row 4: reason (optional) */}
                      {block.reason ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.15,
                            fontSize: '0.7rem',
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={block.reason}
                        >
                          {block.reason}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EventBlockAdjustView;
